import React, { createContext, createRef, useReducer, useRef } from 'react';
import { createHtmlPortalNode } from 'react-reverse-portal';

import {
  alertDetailsPageTestIDs,
  alertsModalPageTestIDs,
  currentWeatherConditionsPageTestIDs,
  dailyWeatherPageTestIDs,
  fullscreenVODPageTestIDs,
  hourlyWeatherPageTestIDs,
  LiveControlsTestIDs,
  livePageTestIDs,
  loadingPageTestIDs,
  mapPageTestIDs,
  menuTestIDs,
  myWeatherPageTestIDs,
  onboardingTestIDs,
  onDemandPageTestIDs,
  pictureDetailedVODPageTestIDs,
  settingsPageTestIDs,
  VODControlsTestIDs,
} from '@constants';

import { DebugItems } from './components/debug/DebugItems';
import { DebugMenuIDs } from './components/debug/DebugMenuIDs';
import { DEBUG, isWeb } from './helpers/Platform';

export const AppContext = createContext([{}, function () {}]);

const { Provider } = AppContext;

const liveVideoPortalNode = isWeb ? createHtmlPortalNode() : null;

export const AppProvider = ({ children }) => {
  // Global state
  const extraReferences = {};

  if (DEBUG) {
    for (const { pageID, component } of DebugItems) {
      if (component) {
        extraReferences[pageID] = createRef(null);
      }
    }
    extraReferences[DebugMenuIDs.debugMenuPage] = createRef(null);
  }
  const [state, setState] = useReducer((oldState, newState) => ({ ...oldState, ...newState }), {
    liveVideoNode: liveVideoPortalNode,
    globalReferences: {
      // helper values
      lastRoute: useRef(null),
      // pages
      [myWeatherPageTestIDs.myWeatherPage]: useRef(null),
      [currentWeatherConditionsPageTestIDs.currentWeatherConditionsPage]: useRef(null),
      [hourlyWeatherPageTestIDs.hourlyWeatherPage]: useRef(null),
      [dailyWeatherPageTestIDs.dailyWeatherPage]: useRef(null),
      [livePageTestIDs.livePage]: useRef(null),
      [fullscreenVODPageTestIDs.fullscreenVODPage]: useRef(null),
      [onDemandPageTestIDs.onDemandPage]: useRef(null),
      [pictureDetailedVODPageTestIDs.pictureDetailedVODPage]: useRef(null),
      [settingsPageTestIDs.settingsPage]: useRef(null),
      [fullscreenVODPageTestIDs.fullscreenVODPage]: useRef(null),
      [mapPageTestIDs.mapPage]: useRef(null),
      [mapPageTestIDs.changeMapPage]: useRef(null),
      [alertDetailsPageTestIDs.alertDetailsPage]: useRef(null),
      [alertsModalPageTestIDs.alertsModalPage]: useRef(null),
      //don't go here unless logging out
      [loadingPageTestIDs.loadingPage]: useRef(null),
      // menu
      [menuTestIDs.mainMenu]: useRef(null),
      [menuTestIDs.showMainMenu]: useRef(null),
      [menuTestIDs.live]: useRef(null),
      [menuTestIDs.anf]: useRef(null),
      [menuTestIDs.myWeather]: useRef(null),
      [menuTestIDs.onDemand]: useRef(null),
      [menuTestIDs.settings]: useRef(null),
      [menuTestIDs.debug]: useRef(null),
      // onboarding
      [onboardingTestIDs.pages.onboardingHomePage]: useRef(null),
      [onboardingTestIDs.pages.onboardingFreeTrialPage]: useRef(null),
      [onboardingTestIDs.pages.deviceActivationPage]: useRef(null),
      [onboardingTestIDs.pages.subscriberSignUpPage]: useRef(null),
      [onboardingTestIDs.pages.subscriberSignInPage]: useRef(null),
      [onboardingTestIDs.pages.authVerificationPage]: useRef(null),
      [onboardingTestIDs.pages.subscriptionSelectionPage]: useRef(null),
      [onboardingTestIDs.pages.subscriberWelcomeBackPage]: useRef(null),
      [onboardingTestIDs.pages.successPage]: useRef(null),
      [onboardingTestIDs.pages.errorPage]: useRef(null),
      // video
      [LiveControlsTestIDs.liveVideoControls]: useRef(null),
      [LiveControlsTestIDs.liveVideoControlsExit]: useRef(null),
      [VODControlsTestIDs.vodVideoControls]: useRef(null),
      [LiveControlsTestIDs.showLiveVideoControls]: useRef(null),
      ...extraReferences,
    },
  });

  return <Provider value={[state, setState]}>{children}</Provider>;
};

AppProvider.context = AppContext;
